import React, { useState, useEffect } from 'react'
import { Search, Filter, Users, Upload, X, Loader } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from './ui/card'
import { Label } from './ui/label'
import { Input } from './ui/input'
import { Button } from './ui/button'
import { Checkbox } from './ui/checkbox'
import { supabase } from '@/integrations/supabase/client'
import { API_BASE_URL, authedFetch } from '@/utils/apiConfig'
import { useToast } from '@/hooks/use-toast'
import * as XLSX from 'xlsx'
import DatePicker from 'react-datepicker'

interface Employee {
  id: string
  name: string
  email: string
  department: string
  role: string
  sicil?: string
  kod?: string
}

interface TestAssignmentFormProps {
  test: any
  onComplete: () => void
  onAssignmentsUpdated: () => void
}

interface AssignmentResult {
  mode: 'sync' | 'async'
  completed: boolean
  job_id?: string
  poll_url?: string
  successful_assignments?: any[]
  failed_assignments?: any[]
  summary?: {
    total_requested: number
    total_successful: number
    total_failed: number
  }
}

interface JobProgress {
  job_id: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  progress: {
    total: number
    processed: number
    successful: number
    failed: number
  }
  started_at?: string
  completed_at?: string
  error?: string
  failed_ids?: string[]
}

const TestAssignmentForm = ({
  test,
  onComplete,
  onAssignmentsUpdated,
}: TestAssignmentFormProps) => {
  const { toast } = useToast()
  const [employees, setEmployees] = useState<Employee[]>([])
  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([])

  const now = new Date()
  const weekLater = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
  const [assignmentStartDate, setAssignmentStartDate] = useState<Date | null>(now)
  const [assignmentDueDate, setAssignmentDueDate] = useState<Date | null>(weekLater)

  const [searchQuery, setSearchQuery] = useState('')
  const [departmentFilter, setDepartmentFilter] = useState('')
  const [positionFilter, setPositionFilter] = useState('')
  const [uploadedSicils, setUploadedSicils] = useState<string[]>([])
  const [fileProcessing, setFileProcessing] = useState(false)
  const [assignedEmployeeIds, setAssignedEmployeeIds] = useState<Set<string>>(new Set())
  const [activeTab, setActiveTab] = useState<'manual' | 'upload'>('manual')

  // Assignment result (sync or async)
  const [assignmentResult, setAssignmentResult] = useState<AssignmentResult | null>(null)
  const [jobProgress, setJobProgress] = useState<JobProgress | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [pollInterval, setPollInterval] = useState<NodeJS.Timeout | null>(null)

  useEffect(() => {
    ;(async () => {
      setLoading(true)
      let allData: any[] = []
      let page = 0
      const pageSize = 1000

      try {
        while (true) {
          const { data, error } = await supabase
            .from('employee')
            .select('*')
            .range(page * pageSize, (page + 1) * pageSize - 1)

          if (error) {
            toast({
              title: 'Hata',
              description: 'Çalışanlar yüklenirken bir hata oluştu.',
              variant: 'destructive',
            })
            break
          }

          if (!data || data.length === 0) break

          allData = [...allData, ...data]
          page++
        }

        const transformedEmployees: Employee[] = (allData || []).map((item) => ({
          id: item.id,
          name: item.personel || 'N/A',
          email: item.mail_adresi || 'N/A',
          department: item.magaza || 'N/A',
          role: item.pozisyon || 'N/A',
          sicil: item.sicil,
          kod: item.kod,
        }))
        setEmployees(transformedEmployees)
        setFilteredEmployees(transformedEmployees)
      } catch (error) {
        console.error('Error loading employees:', error)
      } finally {
        setLoading(false)
      }
    })()
  }, [toast])

  useEffect(() => {
    if (!test?.id) return
    ;(async () => {
      const { data } = await supabase
        .from('employee_tests')
        .select('employee_id')
        .eq('test_id', test.id)
      setAssignedEmployeeIds(new Set((data || []).map((e) => e.employee_id)))
    })()
  }, [test])

  useEffect(() => {
    let filtered = employees
    if (searchQuery) {
      filtered = filtered.filter(
        (emp) =>
          emp.name.toLocaleLowerCase('tr').includes(searchQuery.toLocaleLowerCase('tr')) ||
          emp.email.toLocaleLowerCase('tr').includes(searchQuery.toLocaleLowerCase('tr')) ||
          (emp.sicil && emp.sicil.toLocaleLowerCase('tr').includes(searchQuery.toLocaleLowerCase('tr'))) ||
          (emp.kod && emp.kod.toLocaleLowerCase('tr').includes(searchQuery.toLocaleLowerCase('tr')))
      )
    }
    if (departmentFilter && departmentFilter !== 'all') {
      filtered = filtered.filter((emp) => emp.department === departmentFilter)
    }
    if (positionFilter && positionFilter !== 'all') {
      filtered = filtered.filter((emp) => emp.role === positionFilter)
    }
    setFilteredEmployees(filtered)
  }, [employees, searchQuery, departmentFilter, positionFilter])

  // Poll job progress
  const pollJobStatus = async (jobId: string) => {
    try {
      const response = await authedFetch(`${API_BASE_URL}/api/assignments/jobs/${jobId}`)
      if (!response.ok) {
        throw new Error('Failed to fetch job status')
      }
      const data: JobProgress = await response.json()
      setJobProgress(data)

      // If job is completed or failed, stop polling
      if (data.status === 'completed' || data.status === 'failed') {
        if (pollInterval) {
          clearInterval(pollInterval)
          setPollInterval(null)
        }

        if (data.status === 'completed') {
          toast({
            title: 'Atama Tamamlandı',
            description: `${data.progress.successful} çalışan başarıyla atandı.`,
          })
          onAssignmentsUpdated()
          // Close form after a delay
          setTimeout(() => onComplete(), 2000)
        } else {
          toast({
            title: 'Atama Başarısız',
            description: data.error || 'Bilinmeyen bir hata oluştu.',
            variant: 'destructive',
          })
        }
      }
    } catch (error) {
      console.error('Error polling job status:', error)
    }
  }

  useEffect(() => {
    // Start polling if we have a job ID
    if (assignmentResult?.job_id && assignmentResult.mode === 'async') {
      const interval = setInterval(() => {
        pollJobStatus(assignmentResult.job_id!)
      }, 2000) // Poll every 2 seconds

      setPollInterval(interval)

      return () => {
        if (interval) clearInterval(interval)
      }
    }
  }, [assignmentResult?.job_id])

  const departments = Array.from(
    new Set(employees.map((emp) => emp.department).filter((dept) => dept !== 'N/A'))
  )
  const positions = Array.from(
    new Set(employees.map((emp) => emp.role).filter((role) => role !== 'N/A'))
  )
  const handleEmployeeToggle = (employeeId: string) => {
    setSelectedEmployees((prev) =>
      prev.includes(employeeId) ? prev.filter((id) => id !== employeeId) : [...prev, employeeId]
    )
  }
  const handleSelectAll = () => {
    const selectable = filteredEmployees.filter((emp) => !assignedEmployeeIds.has(emp.id))
    setSelectedEmployees(
      selectedEmployees.length === selectable.length ? [] : selectable.map((emp) => emp.id)
    )
  }

  const clearFilters = () => {
    setSearchQuery('')
    setDepartmentFilter('')
    setPositionFilter('')
  }

  // Sicil upload logic
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    setFileProcessing(true)
    try {
      let sicils: string[] = []
      if (file.name.endsWith('.csv')) {
        const text = await file.text()
        sicils = text
          .split('\n')
          .map((line) => line.split(',')[0].trim().replace(/"/g, ''))
          .filter((s) => s && s !== 'sicil' && s !== 'Sicil')
      } else if (file.name.endsWith('.txt')) {
        const text = await file.text()
        sicils = text
          .split('\n')
          .map((line) => line.trim())
          .filter((line) => line && line !== 'sicil' && line !== 'Sicil')
      } else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        const data = await file.arrayBuffer()
        const workbook = XLSX.read(data, { type: 'array' })
        const firstSheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[firstSheetName]
        const sheetData: any[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1 })
        // Sütun başlığı "sicil" veya "Sicil" olanı bul
        const headerRow = sheetData[0] as string[]
        const sicilIndex = headerRow.findIndex(
          (h) => h?.toLowerCase().replace(/"/g, '') === 'sicil'
        )
        if (sicilIndex === -1) {
          toast({
            title: 'Excel Hatası',
            description: 'Excel dosyasında "sicil" başlıklı bir sütun bulunamadı.',
            variant: 'destructive',
          })
          return
        }
        sicils = sheetData
          .slice(1)
          .map((row) => (row[sicilIndex] ? String(row[sicilIndex]).trim() : ''))
          .filter((s) => s && s.toLowerCase() !== 'sicil')
      } else {
        toast({
          title: 'Desteklenmeyen Dosya',
          description: 'CSV, Excel veya TXT dosyası yükleyin.',
          variant: 'destructive',
        })
        return
      }
      const uniqueSicils = [...new Set(sicils)].filter((sicil) => sicil.length > 0)
      setUploadedSicils(uniqueSicils)

      // Create a map to store first matches
      const sicilToEmployeeMap = new Map()

      // Get first match for each sicil
      uniqueSicils.forEach((sicil) => {
        const matchingEmployee = employees.find((emp) => emp.sicil === sicil)
        if (matchingEmployee) {
          sicilToEmployeeMap.set(sicil, matchingEmployee)
        }
      })

      // Get only the first matches
      const matchingEmployees = Array.from(sicilToEmployeeMap.values())
      setSelectedEmployees(matchingEmployees.map((emp) => emp.id))

      toast({
        title: 'Dosya Yüklendi',
        description: `${uniqueSicils.length} sicil, ${matchingEmployees.length} çalışan eşleştirildi.`,
      })
    } catch (error) {
      toast({
        title: 'Dosya Hatası',
        description: 'Dosya işlenirken bir hata oluştu.',
        variant: 'destructive',
      })
    } finally {
      setFileProcessing(false)
      event.target.value = ''
    }
  }
  const clearUploadedSicils = () => {
    setUploadedSicils([])
    setSelectedEmployees([])
  }

  // --- TEST ASSIGNMENT ---
  const assignEmployeesToTestBulk = async ({
    testId,
    employeeIds,
    assignmentStartDate,
    assignmentDueDate,
  }: {
    testId: string
    employeeIds: string[]
    assignmentStartDate: string
    assignmentDueDate: string
  }) => {
    const response = await authedFetch(`${API_BASE_URL}/api/assignments/generate_assignment`, {
      method: 'POST',
      body: JSON.stringify({
        testId,
        employeeIds,
        assignment_start_date: assignmentStartDate,
        assignment_due_date: assignmentDueDate,
      }),
    })
    if (!response.ok) {
      const error = await response.text()
      throw new Error(error || 'Bulk assignment failed')
    }
    return response.json()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setAssignmentResult(null)
    setJobProgress(null)

    if (selectedEmployees.length === 0) {
      toast({
        title: 'Seçim Yapılmadı',
        description: 'Lütfen en az bir çalışan seçin.',
        variant: 'destructive',
      })
      return
    }

    setIsSubmitting(true)
    try {
      const startDate = assignmentStartDate
      const dueDate = assignmentDueDate

      if (!startDate || !dueDate) {
        toast({
          title: 'Tarih Eksik',
          description: 'Başlangıç ve bitiş tarihlerini seçin.',
          variant: 'destructive',
        })
        setIsSubmitting(false)
        return
      }

      // Add validation for due date being after start date
      if (dueDate <= startDate) {
        toast({
          title: 'Geçersiz Tarih',
          description: 'Bitiş tarihi başlangıç tarihinden sonra olmalıdır.',
          variant: 'destructive',
        })
        setIsSubmitting(false)
        return
      }

      const result: AssignmentResult = await assignEmployeesToTestBulk({
        testId: test.id,
        employeeIds: selectedEmployees,
        assignmentStartDate: startDate.toISOString(),
        assignmentDueDate: dueDate.toISOString(),
      })

      setAssignmentResult(result)

      if (result.mode === 'sync') {
        // Sync mode - show results immediately
        toast({
          title: 'Başarılı!',
          description: `${result.summary?.total_successful} çalışana başarıyla atandı.`,
        })
        onAssignmentsUpdated()
        setTimeout(() => onComplete(), 2000)
      } else {
        // Async mode - show job queued message and start polling
        toast({
          title: 'Atama Sıraya Alındı',
          description: `${selectedEmployees.length} çalışan için atama işlemi başlatıldı. İlerleme izlenecek.`,
        })
      }
    } catch (e: any) {
      toast({
        title: 'Atama Hatası',
        description: e.message || 'Çalışanlara atama yapılırken bir hata oluştu.',
        variant: 'destructive',
      })
      setIsSubmitting(false)
    }
  }

  if (loading) {
    return (
      <Card className="w-full shadow-md">
        <CardContent className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </CardContent>
      </Card>
    )
  }

  const matchingEmployeesFromSicils =
    uploadedSicils.length > 0
      ? uploadedSicils
          .map((sicil) => employees.find((emp) => emp.sicil === sicil))
          .filter((emp): emp is Employee => emp !== undefined)
      : []

  const unassignedCount = filteredEmployees.filter((emp) => !assignedEmployeeIds.has(emp.id)).length

  return (
    <Card className="w-full shadow-md">
      <CardHeader>
        <CardTitle>
          Çalışanlara Sınav Ata
          {test?.name && (
            <span className="text-lg font-normal text-gray-600 ml-2">- {test.name}</span>
          )}
        </CardTitle>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 space-y-2">
              <Label htmlFor="assignmentStartDate">Sınav Başlangıç Tarihi</Label>
              <div className="relative">
                <DatePicker
                  id="assignmentStartDate"
                  selected={assignmentStartDate}
                  onChange={(date: Date | null) => setAssignmentStartDate(date)}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="yyyy-MM-dd HH:mm"
                  minDate={new Date()}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholderText="Tarih ve saat seçin"
                />
              </div>
            </div>

            <div className="flex-1 space-y-2">
              <Label htmlFor="dueDate">Sınav Bitiş Tarihi</Label>
              <div className="relative">
                <DatePicker
                  id="dueDate"
                  selected={assignmentDueDate}
                  onChange={(date: Date | null) => setAssignmentDueDate(date)}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="yyyy-MM-dd HH:mm"
                  minDate={assignmentStartDate || new Date()}
                  minTime={
                    assignmentStartDate &&
                    assignmentDueDate &&
                    assignmentStartDate.toDateString() === assignmentDueDate.toDateString()
                      ? assignmentStartDate
                      : new Date(new Date().setHours(0, 0, 0, 0))
                  }
                  maxTime={new Date(new Date().setHours(23, 59, 59, 999))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholderText="Tarih ve saat seçin"
                />
              </div>
            </div>
          </div>

          {/* Only show tabs if not in async processing */}
          {!assignmentResult?.job_id && (
            <Tabs
              value={activeTab}
              onValueChange={(value) => setActiveTab(value as 'manual' | 'upload')}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="manual">Manuel Seçim</TabsTrigger>
                <TabsTrigger value="upload">Dosyadan Yükle</TabsTrigger>
              </TabsList>

              <TabsContent value="manual" className="space-y-6">
                <div className="space-y-4 border rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center gap-2 mb-3">
                    <Filter className="h-4 w-4 text-gray-500" />
                    <Label className="text-sm font-medium">Çalışan Arama ve Filtreleme</Label>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Ad, e-posta, sicil ile ara..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                    <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="Mağaza Seçin" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="all">Tüm Mağazalar</SelectItem>
                        {departments.map((dept) => (
                          <SelectItem key={dept} value={dept}>
                            {dept}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={positionFilter} onValueChange={setPositionFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="Pozisyon Seçin" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="all">Tüm Pozisyonlar</SelectItem>
                        {positions.map((pos) => (
                          <SelectItem key={pos} value={pos}>
                            {pos}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {(searchQuery || departmentFilter || positionFilter) && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">
                        {unassignedCount} çalışan atanmaya hazır
                      </span>
                      <Button type="button" variant="ghost" size="sm" onClick={clearFilters}>
                        Filtreleri Temizle
                      </Button>
                    </div>
                  )}
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <Label>Çalışan Seçimi</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleSelectAll}
                      disabled={unassignedCount === 0}
                    >
                      {selectedEmployees.length === unassignedCount
                        ? 'Tümünü Kaldır'
                        : 'Tümünü Seç'}
                    </Button>
                  </div>
                  {filteredEmployees.length > 0 ? (
                    <div className="border rounded-md divide-y max-h-80 overflow-y-auto">
                      {filteredEmployees.map((employee) => {
                        const isDisabled = assignedEmployeeIds.has(employee.id)
                        return (
                          <div
                            key={employee.id}
                            className={`p-3 flex items-center gap-3 hover:bg-gray-50 
                        ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <Checkbox
                              id={`employee-${employee.id}`}
                              checked={selectedEmployees.includes(employee.id)}
                              onCheckedChange={() =>
                                !isDisabled && handleEmployeeToggle(employee.id)
                              }
                              disabled={isDisabled}
                            />
                            <div className="flex-1">
                              <Label
                                htmlFor={`employee-${employee.id}`}
                                className="font-medium cursor-pointer"
                              >
                                {employee.name}
                              </Label>
                              <div className="flex gap-4 text-sm text-gray-600 mt-1">
                                <span>{employee.department}</span>
                                <span>{employee.role}</span>
                                {employee.sicil && <span>Sicil: {employee.sicil}</span>}
                              </div>
                              <div className="text-sm text-gray-500 mt-1">{employee.email}</div>
                              {isDisabled && (
                                <div className="text-xs text-yellow-700 font-medium mt-1">
                                  Bu çalışan zaten atandı.
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-md">
                      <p className="text-gray-500">
                        {searchQuery || departmentFilter || positionFilter
                          ? 'Arama kriterine uygun çalışan bulunamadı.'
                          : 'Çalışan bulunamadı.'}
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>
              <TabsContent value="upload" className="space-y-4">
                <div className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center gap-2 mb-3">
                    <Upload className="h-4 w-4 text-gray-500" />
                    <Label className="text-sm font-medium">Sicil Numarası Dosyası Yükle</Label>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Input
                        type="file"
                        accept=".csv,.txt,.xlsx,.xls"
                        onChange={handleFileUpload}
                        disabled={fileProcessing}
                        className="cursor-pointer"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        CSV veya Excel dosyası yükleyin. Her satırda bir sicil numarası olmalı.
                      </p>
                    </div>
                    {uploadedSicils.length > 0 && (
                      <div className="bg-white rounded-md border p-3">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium">
                            Yüklenen Sicil Numaraları ({uploadedSicils.length})
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={clearUploadedSicils}
                          >
                            <X className="h-4 w-4" />
                            Temizle
                          </Button>
                        </div>
                        <div className="max-h-32 overflow-y-auto">
                          <div className="grid grid-cols-4 gap-2 text-xs">
                            {uploadedSicils.map((sicil, index) => (
                              <span key={index} className="bg-gray-100 px-2 py-1 rounded">
                                {sicil}
                              </span>
                            ))}
                          </div>
                        </div>
                        {matchingEmployeesFromSicils.length > 0 && (
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-sm font-medium mb-2">
                              Eşleşen Çalışanlar ({matchingEmployeesFromSicils.length})
                            </p>
                            <div className="space-y-1 max-h-32 overflow-y-auto">
                              {matchingEmployeesFromSicils.map((emp) => (
                                <div key={emp.id} className="text-xs bg-blue-50 p-2 rounded">
                                  <div className="font-medium">{emp.name}</div>
                                  <div className="text-gray-600">
                                    Sicil: {emp.sicil} | {emp.department} | {emp.role}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Show unmatched sicil numbers */}
                        {uploadedSicils.length > 0 && (
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-sm font-medium mb-2">
                              Eşleşmeyen Sicil Numaraları (
                              {
                                uploadedSicils.filter(
                                  (sicil) => !employees.some((emp) => emp.sicil === sicil)
                                ).length
                              }
                              )
                            </p>
                            <div className="space-y-1 max-h-32 overflow-y-auto">
                              {uploadedSicils
                                .filter((sicil) => !employees.some((emp) => emp.sicil === sicil))
                                .map((sicil, index) => (
                                  <div
                                    key={index}
                                    className="text-xs bg-red-50 p-2 rounded border border-red-200"
                                  >
                                    <div className="font-medium text-red-700">Sicil: {sicil}</div>
                                    <div className="text-red-600 text-xs">Çalışan bulunamadı</div>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          )}

          <div className="bg-blue-50 p-3 rounded-md">
            <p className="text-sm text-blue-800">
              <span className="font-medium">{selectedEmployees.length}</span> çalışan seçildi
              {uploadedSicils.length > 0 && (
                <span className="ml-2 text-xs">
                  ({uploadedSicils.length} sicil yüklendi, {matchingEmployeesFromSicils.length}{' '}
                  eşleşti)
                </span>
              )}
            </p>
          </div>

          {/* Sync Mode Results */}
          {assignmentResult?.mode === 'sync' && assignmentResult?.completed && (
            <Card className="border-green-200 bg-green-50">
              <CardHeader>
                <CardTitle className="text-green-800">Atama Tamamlandı</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Başarılı</p>
                    <p className="text-2xl font-bold text-green-600">
                      {assignmentResult.summary?.total_successful}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Başarısız</p>
                    <p className="text-2xl font-bold text-red-600">
                      {assignmentResult.summary?.total_failed}
                    </p>
                  </div>
                </div>
                {assignmentResult.failed_assignments &&
                  assignmentResult.failed_assignments.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-green-200">
                      <p className="text-sm font-medium text-red-700 mb-2">Başarısız Atamalar:</p>
                      <ul className="list-disc pl-4 text-xs text-red-600 max-h-40 overflow-y-auto">
                        {assignmentResult.failed_assignments.map((fail: any) => (
                          <li key={fail.employeeId}>
                            {fail.employeeId}: {fail.error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
              </CardContent>
            </Card>
          )}

          {/* Async Mode Progress */}
          {assignmentResult?.mode === 'async' && jobProgress && (
            <Card className="border-blue-200 bg-blue-50">
              <CardHeader>
                <CardTitle className="text-blue-800 flex items-center gap-2">
                  <Loader className="h-5 w-5 animate-spin" />
                  Atama İşlemi Devam Ediyor
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">
                      {jobProgress.progress.processed} / {jobProgress.progress.total} işlendi
                    </span>
                    <span className="font-medium text-gray-800">
                      %
                      {Math.round(
                        (jobProgress.progress.processed / jobProgress.progress.total) * 100
                      )}
                    </span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${(jobProgress.progress.processed / jobProgress.progress.total) * 100}%`,
                      }}
                    ></div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="bg-white p-2 rounded border border-blue-200">
                    <p className="text-gray-600 text-xs">Başarılı</p>
                    <p className="text-lg font-bold text-green-600">
                      {jobProgress.progress.successful}
                    </p>
                  </div>
                  <div className="bg-white p-2 rounded border border-blue-200">
                    <p className="text-gray-600 text-xs">Başarısız</p>
                    <p className="text-lg font-bold text-red-600">{jobProgress.progress.failed}</p>
                  </div>
                  <div className="bg-white p-2 rounded border border-blue-200">
                    <p className="text-gray-600 text-xs">Durum</p>
                    <p className="text-lg font-bold text-blue-600 capitalize">
                      {jobProgress.status === 'processing' ? 'İşleniyor' : jobProgress.status}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={onComplete}
            disabled={isSubmitting || assignmentResult?.mode === 'async'}
          >
            İptal
          </Button>
          <Button
            type="submit"
            disabled={selectedEmployees.length === 0 || isSubmitting || loading}
          >
            {isSubmitting ? (
              <>
                <Loader className="mr-2 h-4 w-4 animate-spin" />
                İşleniyor...
              </>
            ) : (
              `Testi Ata (${selectedEmployees.length} çalışan)`
            )}
          </Button>
        </CardFooter>
      </form>
    </Card>
  )
}

export default TestAssignmentForm
